import React, { useEffect, useState, useCallback } from 'react';
import { Modal, ModalBody } from '../common';

export interface SubscriptionFeature {
  name: string;
}

interface SubscriptionModalProps {
  /** Unique ID for this prompt (used for session storage) */
  promptId: string;
  /** List of subscription features to display */
  features: SubscriptionFeature[];
  /** URL to view subscription plans */
  subscriptionUrl: string;
  /** URL to dismiss the subscription prompt */
  dismissUrl: string;
  /** CSRF token for API requests */
  csrfToken: string;
  /** Translations for modal content */
  translations: {
    unlockPremiumFeatures: string;
    getUnlimitedAccess: string;
    whatYouGetWithPremium: string;
    viewSubscriptionPlans: string;
    maybeLater: string;
    noCommitment: string;
    close: string;
  };
}

/**
 * SubscriptionModal - Prompts users to subscribe to premium features
 *
 * Ported from:
 * - app/views/diaries/shared/_subscription_modal.html.erb
 * - app/javascript/controllers/subscription_modal_controller.js
 */
export function SubscriptionModal({
  promptId,
  features,
  subscriptionUrl,
  dismissUrl,
  csrfToken,
  translations,
}: SubscriptionModalProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Check session storage to avoid showing the same modal twice
  useEffect(() => {
    const sessionKey = `subscription_modal_shown_${promptId}`;

    if (sessionStorage.getItem(sessionKey)) {
      return;
    }

    sessionStorage.setItem(sessionKey, 'true');
    setIsOpen(true);
  }, [promptId]);

  const sendDismissRequest = useCallback(() => {
    fetch(dismissUrl, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': csrfToken,
        Accept: 'text/vnd.turbo-stream.html',
      },
    });
  }, [dismissUrl, csrfToken]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    sendDismissRequest();
  }, [sendDismissRequest]);

  const handleViewPlans = useCallback(() => {
    setIsOpen(false);
  }, []);

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      ariaLabel={translations.unlockPremiumFeatures}
      centered
    >
      <ModalBody className="p-0">
        {/* Header Section with Gradient */}
        <div className="subscription-modal-header text-center p-4 position-relative overflow-hidden">
          <button
            type="button"
            className="btn-close btn-close-white position-absolute top-0 end-0 m-3"
            onClick={handleClose}
            aria-label={translations.close}
          />
          <div className="subscription-modal-bg-animation"></div>
          <div className="position-relative z-1">
            <div className="d-inline-block position-relative mb-2">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center bg-warning bg-opacity-20 mx-auto"
                style={{ width: 64, height: 64 }}
              >
                <i className="bi bi-star-fill text-warning" style={{ fontSize: '2rem' }}></i>
              </div>
              <span
                className="position-absolute top-0 end-0 badge rounded-circle bg-danger p-1"
                style={{ fontSize: '0.6rem' }}
              >
                <i className="bi bi-arrow-up"></i>
              </span>
            </div>
            <h4 className="mb-2 fw-bold text-white">{translations.unlockPremiumFeatures}</h4>
            <p className="text-white-50 mb-0 small">{translations.getUnlimitedAccess}</p>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="p-3">
          <h6 className="fw-bold mb-2 text-center">{translations.whatYouGetWithPremium}</h6>
          <div className="row g-2 mb-3">
            {features.slice(0, 4).map((feature, index) => (
              <div key={index} className="col-6">
                <div className="d-flex align-items-start p-2 rounded-3 bg-light">
                  <div className="flex-shrink-0 me-2">
                    <div
                      className="rounded-circle bg-success bg-opacity-10 d-flex align-items-center justify-content-center"
                      style={{ width: 28, height: 28 }}
                    >
                      <i className="bi bi-check-lg text-success small"></i>
                    </div>
                  </div>
                  <div>
                    <small className="fw-semibold d-block lh-sm">{feature.name}</small>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="d-grid gap-2">
            <a
              href={subscriptionUrl}
              className="btn btn-primary"
              onClick={handleViewPlans}
              data-testid="view-subscription-plans"
            >
              <i className="bi bi-star-fill me-2"></i>
              {translations.viewSubscriptionPlans}
            </a>

            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={handleClose}
              data-testid="dismiss-subscription-modal"
            >
              {translations.maybeLater}
            </button>
          </div>

          <p className="text-center text-muted small mb-0 mt-2">{translations.noCommitment}</p>
        </div>
      </ModalBody>
    </Modal>
  );
}
